

import {
  IconCircleCheck,
  IconFilter,
  IconFilterFilled,
} from "@tabler/icons-react";
import { Company } from "@/types/Company";
import { Event } from "@/types/Event";
import { User } from "@/types/User";
import { useLocale, useTranslations } from "next-intl";
import { Card } from "../Card/Card";
import { NotificationChip } from "./NotificationChip";
import { PreflightResult, usePNotifications } from "./usePNotifications";
import { JoinCompanyDialog } from "./JoinCompanyDialog";
import { UserLog } from "@/types/UserLog";
import { PDeleteConfirmation } from "../PDeleteConfirmation/PDeleteConfirmation";
import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { IconTooltip } from "../IconTooltip/IconTooltip";
import { NotificationStateType } from "../AppBar/hooks/useNotifications";
import { ServerError } from "@/lib/api/errors/ErrorHandler";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { reasonTextFromError } from "@/components/PDeleteCompany/dissolveText";
import { useToast } from "@/components/Toast/Toast";
import { VideoPlayerRiv } from "@/components/VideoPlayer/VideoPlayerRiv";
import { useBackgroundJobs } from "@/providers/BackgroundJobsProvider";
import { BackgroundJobsList } from "../PBackgroundJobs/PBackgroundJobs";
import { TabSwitcher } from "../Buttons/TabSwitcher";
import { isRequestNotification, requestHref, requestOpenMarksRead } from "./requestLink";
import { IconCalendarPlus, IconUserPlus, IconUsersPlus } from "@tabler/icons-react";

/** Types whose `place` is an organisation rather than an event. */
const COMPANY_SCOPED_TYPES = new Set([
  "COMPANY_OWNERSHIP_TRANSFERRED",
  "COMPANY_OWNERSHIP_GOTTEN",
  "COMPANY_INVITATION",
  "COMPANY_INVITATION_ACCEPTED",
  "COMPANY_INVITATION_REJECTED",
  "COMPANY_INVITATION_CANCELLED",
  "COMPANY_ROLE_CHANGED",
  "COMPANY_ROLE_DELETED",
  "AI_QUOTA_LOW",
  "AI_QUOTA_EXHAUSTED",
]);

/**
 * Types that are never triggered by a person. The backend now says so per row
 * via `actorless`; this list is the fallback for responses that predate it, so
 * an old backend still doesn't paint a "D" monogram on a system message.
 */
const TYPES_WITHOUT_ACTOR = new Set([
  "EVENT_INVITATION_ACCEPTED",
  "EVENT_INVITATION_DECLINED",
  "EVENT_TRANSFER_INVITATION_DECLINED",
  "EVENT_TRANSFER_INVITATION_SENT",
  "FAILED_NO_ROLE",
  "COMPANY_INVITATION_ACCEPTED",
  "COMPANY_INVITATION_REJECTED",
  "COMPANY_OWNERSHIP_TRANSFERRED",
  "AI_QUOTA_LOW",
  "AI_QUOTA_EXHAUSTED",
  // Offboarding Q11: the backend writes no responsibleUserId for it.
  "EVENT_ACCESS_ENDED_TRANSFER",
  // W4-d: a request announces itself — the guest is not a cockpit user.
  "GUEST_REQUEST_SUBMITTED",
  "COMPANION_REQUEST_SUBMITTED",
  "APPOINTMENT_REQUEST_SUBMITTED",
]);

/** W4-d: the row's icon per request kind — never a monogram, the guest is no user. */
function requestIcon(type: string): React.ReactNode {
  switch (type) {
    case "GUEST_REQUEST_SUBMITTED":
      return <IconUserPlus size={18} className="font-blue" />;
    case "COMPANION_REQUEST_SUBMITTED":
      return <IconUsersPlus size={18} className="font-blue" />;
    case "APPOINTMENT_REQUEST_SUBMITTED":
      return <IconCalendarPlus size={18} className="font-blue" />;
    default:
      return undefined;
  }
}

type PNotificationsProps = {
  ref: React.RefObject<HTMLDivElement | null>;
  notificationState: NotificationStateType;
  toggleNotificationViewAction: () => void;
  handleNotificationUpdateAction: (
    id: string,
    actionMethod: (
      id: string
    ) => Promise<void | ServerError> | Promise<string | ServerError>,
    notification: UserLog
  ) => Promise<void>;
  markAllNotificationsAsReadAction: () => void;
  markNotificationAsReadAction: (id: string) => void;
  fetchAdditionalNotificationsAction: () => void;
  fetchAdditionalUnreadNotificationsAction: () => void;
  /**
   * W4-d (lens 6 #12): called right before a request row navigates — the
   * app bar closes the bell popup with it, so the page does not change
   * behind a popup that stays open until the next outside click.
   */
  onNavigateAction?: () => void;
};

export const PNotifications: React.FC<PNotificationsProps> = ({
  ref,
  notificationState,
  toggleNotificationViewAction,
  handleNotificationUpdateAction,
  markAllNotificationsAsReadAction,
  markNotificationAsReadAction,
  fetchAdditionalNotificationsAction,
  fetchAdditionalUnreadNotificationsAction,
  onNavigateAction,
}) => {
  const t = useTranslations("popup-notifications");
  const t2 = useTranslations("tooltips");
  const tErrors = useTranslations("errors");
  // PB-CDP: the dissolve gate's reason and loss texts live in `userSettings`
  // (`dissolveBlock.switch.*`, `dissolveLosses.*`); the locale formats the
  // CANCELLING date.
  const tUser = useTranslations("userSettings");
  const locale = useLocale();
  const { showToast } = useToast();
  const { actionAccept, actionReject, confirmCompanyJoin } = usePNotifications(
    handleNotificationUpdateAction
  );
  /**
   * A failed notification action, described as precisely as we can honestly be.
   *
   * Deliberately an ALLOWLIST rather than "translate whatever code arrives".
   * The `errors` namespace has hundreds of keys written for other screens, and
   * a code that reads fine there can be nonsense here — the switch's own
   * duplicate-key failure surfaced as `nameAlreadyExists`, i.e. "The name
   * already exists", which names nothing the user did. Showing that is worse
   * than the generic sentence, not better. Codes not on this list fall back to
   * the caller's own sentence.
   */
  const NAMEABLE_ERROR_CODES = useMemo(
    () =>
      new Set([
        "collaboratorLimitReached",
        "subscriptionRequired",
        "invalidInviteCode",
        "cannotRemoveOwnerWithUsers",
        "sessionExpired",
        "forbidden",
        "notFound",
        // Catch-all, but the ONLY code for which translateBackendError appends
        // the backend's `detail` — without it here the detail we carry around
        // could never reach a rendered string.
        "validationError",
        // Offboarding HIGH-1/L-1: a transfer whose target already belongs to
        // the event's organisation is refused at the bell accept. Neutral
        // copy — the target reads it here, the initiator in the dialog.
        "transferTargetSameCompany",
        // PB-CDP — the dissolve gate on the switch door: a payment still
        // processing, the check that could not run (503), losses the dialog
        // did not acknowledge (an old tab, or losses that grew); and at an
        // event-transfer accept, a source organisation that is being deleted.
        // (`activeSubscription` is not here: its reason is read below.)
        "paymentPending",
        "subscriptionCheckUnavailable",
        "lossesNotAcknowledged",
        // PB-CDP-7: the switch door could not store the acknowledgement it
        // was given (503) — nothing happened, "try again in a moment".
        "lossesAckNotRecorded",
        "transferSourceDeleting",
        // …and the 503 when that source check itself could not run (FIX-BE r1:
        // its own code — `subscriptionCheckUnavailable` named the wrong check).
        "transferCheckUnavailable",
        // The switch door's own rate-limit bucket (429): "wait a moment" says
        // what helps — "switch failed, try again" sent the user straight back
        // into the same bucket (FIX-FE2, r2 lens 2 LOW2 / lens 3 F2).
        "rateLimited",
      ]),
    [],
  );
  /**
   * @param fallback already-translated sentence for codes we will not name.
   *   It differs per door: a failed event decline must not tell the user their
   *   organisation switch failed.
   */
  const describeFailure = useCallback(
    (failure: { code: string; detail?: string }, fallback: string) =>
      // PB-CDP: the switch door's 400 `activeSubscription:<REASON>[:<sec>]`
      // (a plan Stripe still bills — a Stripe-only verdict shows up only
      // here, never in the preflight) says why, in the switch's words.
      reasonTextFromError(
        tUser,
        { message: failure.code, detail: failure.detail },
        "switch",
        locale,
      ) ??
      (NAMEABLE_ERROR_CODES.has(failure.code)
        ? translateBackendError(
            tErrors,
            { message: failure.code, detail: failure.detail },
            fallback,
          )
        : fallback),
    [tErrors, tUser, locale, NAMEABLE_ERROR_CODES],
  );
  // Two-tab popup: server notifications + the client-side background-jobs list.
  // The bell is the durable home for jobs once the floating panel is dismissed.
  const [tab, setTab] = useState<"notifications" | "tasks">("notifications");
  const { jobs } = useBackgroundJobs();
  const [preflightData, setPreflightData] = useState<PreflightResult | null>(null);
  /**
   * In-flight guard for the notification chips. `disabledButtons` only ever
   * covered a missing id, so a second click on "Join" re-fired the action.
   * The ref is the real guard (state is a render behind, and these handlers
   * are async); the boolean exists to grey the buttons out.
   *
   * Scope, stated honestly: it blocks ALL chips while any one action runs —
   * that is deliberate, since the alternative is a click that silently does
   * nothing on a row the user did not expect to be busy. And it lives and dies
   * with this component, which the app mounts only while the bell popup is
   * open (`AppBarUserControls`): closing and reopening the bell mid-request
   * clears it. That residual window is covered on the server, which refuses a
   * second accept before it reserves anything — the invitation row is already
   * `valid:true`, so `getInvalidCompanyRole` finds nothing and the call is
   * rejected with "No invite exists". This guard is the fast, local half.
   *
   * `notificationState.loadingAction` is not usable here: it is written but
   * never read anywhere in the repo, and the company-invitation branch runs
   * its preflight outside the handleNotificationUpdate paths that set it.
   */
  const chipActionInFlight = useRef(false);
  const [chipBusy, setChipBusy] = useState(false);
  const runChipAction = useCallback(
    async (action: () => Promise<void>) => {
      if (chipActionInFlight.current) return;
      chipActionInFlight.current = true;
      setChipBusy(true);
      try {
        await action();
      } finally {
        chipActionInFlight.current = false;
        setChipBusy(false);
      }
    },
    [],
  );
  // Free-plan company tried to accept an event transfer — explain + upsell
  // instead of a dead click (the backend rejects with subscriptionRequired).
  const [transferUpgradeOpen, setTransferUpgradeOpen] = useState(false);
  const navigate = useNavigate();
  // Reference to the scrollable notifications container
  const notificationsContainerRef = useRef<HTMLDivElement>(null);

  // Implement infinite scroll functionality
  const handleScroll = useCallback(() => {
    if (!notificationsContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      notificationsContainerRef.current;
    const scrollPosition = scrollTop + clientHeight;
    const threshold = scrollHeight - 50; // 50px from bottom triggers loading

    // Check if we've scrolled near the bottom and not already loading
    if (scrollPosition >= threshold && !notificationState.loading) {
      // Based on current view, fetch either all notifications or only unread ones
      if (notificationState.view === "all" && notificationState.hasMore) {
        fetchAdditionalNotificationsAction();
      } else if (
        notificationState.view === "unread" &&
        notificationState.hasMoreUnread
      ) {
        fetchAdditionalUnreadNotificationsAction();
      }
    }
  }, [
    notificationState.loading,
    notificationState.view,
    notificationState.hasMore,
    notificationState.hasMoreUnread,
    fetchAdditionalNotificationsAction,
    fetchAdditionalUnreadNotificationsAction,
  ]);

  // Add scroll event listener
  useEffect(() => {
    const currentContainer = notificationsContainerRef.current;
    if (currentContainer) {
      currentContainer.addEventListener("scroll", handleScroll);
      return () => {
        currentContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  const notificationPanel = createPortal(
    <Card
      ref={ref}
      width="w-[95vw] sm:w-100"
      height="h-[min(37.5rem,80vh)]"
      rounded="rounded-[10px]"
      // Rail offset: track the bell icon, which the layout shell shifts left
      // while the AI drawer rail is open (see PBackgroundJobs).
      className="fixed top-12 right-2 sm:right-[calc(2rem+var(--ai-rail-push))] z-50"
      padding="pl-4"
      justify="justify-top"
    >
      {/* Sticky header — tabs + notification actions stay pinned while the
          list below scrolls, because it lives OUTSIDE the scroll container as
          a direct flex-col child of the Card. `items-stretch` makes the action
          column match the TabSwitcher's height so their shared bottom border
          reads as one continuous line under the whole header. */}
      <div
        className="flex flex-row items-stretch justify-between shrink-0 pr-4 pt-4"
        data-testid="notifications-popup-header"
      >
        <div className="flex-1 min-w-0">
          <TabSwitcher
            tabs={["notifications", "tasks"]}
            currentTab={tab}
            onTabChange={(id) => setTab(id as "notifications" | "tasks")}
            counts={[
              notificationState.numUnread || undefined,
              jobs.length || undefined,
            ]}
            icon={false}
          />
        </div>

        {tab === "notifications" && (
          <div
            className="flex flex-row items-center shrink-0 pl-2 border-b outline-quaternary"
            data-testid="notifications-actions"
          >
            <IconTooltip
              className="p-1"
              icon={
                notificationState.view === "all" ? IconFilter : IconFilterFilled
              }
              tooltip={
                notificationState.view === "all"
                  ? t2("showUnread")
                  : t2("showAll")
              }
              onClick={toggleNotificationViewAction}
            />

            {/* Mark all as read button with tooltip */}
            <IconTooltip
              className="py-1 pl-1"
              icon={IconCircleCheck}
              tooltip={t2("markAllRead")}
              onClick={markAllNotificationsAsReadAction}
              disabled={notificationState.numUnread < 1}
            />
          </div>
        )}
      </div>

      {/* Scrollable list — flex-1 + min-h-0 so it fills the remaining Card
          height and scrolls internally under the pinned header. */}
      <div
        ref={notificationsContainerRef}
        data-testid="notifications-popup-list"
        className="flex flex-col flex-1 min-h-0 pr-4 pt-3 pb-4 overflow-y-auto scroll-smooth custom-scrollbar"
      >
        {tab === "tasks" ? (
          <BackgroundJobsList />
        ) : (
          <>
        {(notificationState.view === "all"
          ? notificationState.notifications
          : notificationState.notificationsUnread
        )?.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1">
            <VideoPlayerRiv videoName="NoNotifications" width="w-35 sm:w-45 lg:w-50" height="h-35 sm:h-45 lg:h-50" />
            <p className="body-2-bold font-secondary p-4">
              {t("noNotificationsCaptain")}
            </p>
          </div>
        ) : (
          (notificationState.view === "all"
            ? notificationState.notifications
            : notificationState.notificationsUnread
          ).map((notification) => {
            const type = notification.type;

            // `place` is absent whenever the referenced event/company is gone
            // (JSON-B omits nulls). Non-null-asserting through it threw a
            // TypeError here, and the only boundary above this map is the root
            // Sentry one — a single dead invitation white-screened the app.
            let id = "";
            if (
              type === "EVENT_INVITATION" ||
              type === "EVENT_TRANSFER_INVITATION"
            ) {
              id = (notification.place as Event | undefined)?.eventId ?? "";
            } else if (type === "COMPANY_INVITATION") {
              id = (notification.place as Company | undefined)?.companyId ?? "";
            }

            // Sentence subject. `placeName` is the resolved name (live entity,
            // else the write-time snapshot); `place.name` is only read as a
            // shim for a backend that predates those fields, where the two
            // English literals below are the tell that the entity is gone.
            const legacyName = notification.place?.name;
            const legacyDeleted =
              legacyName === "DeletedEvent" || legacyName === "DeletedCompany";
            const resolvedName =
              notification.placeName ?? (legacyDeleted ? undefined : legacyName);
            const placeUnresolved = !resolvedName;
            const isCompanyScope = COMPANY_SCOPED_TYPES.has(type);
            const placeLabel = placeUnresolved
              ? t(isCompanyScope ? "deletedCompanyName" : "deletedEventName")
              : resolvedName;

            // An actor-less type has no person behind it; a deleted one has a
            // name we can no longer read. Neither may render as a stray "D".
            const responsible =
              notification.actorDeleted === true ||
              notification.responsibleUser?.firstName === "DeletedUser"
                ? ({
                    ...notification.responsibleUser,
                    firstName: t("deletedUserName"),
                  } as User)
                : notification.responsibleUser;

            // W4-d: a request row is a link — click = open the requests page
            // on the right tab with the subject focused, and the row is read.
            const href = requestHref(notification);
            const isRequest = isRequestNotification(type);

            return (
              <NotificationChip
                onReadClick={() => {
                  markNotificationAsReadAction(notification.logId);
                }}
                onOpen={
                  href
                    ? () => {
                        // Lens 6 #10: an appointment row's link only reaches
                        // the programme page (no offeringId on the row yet),
                        // so it is NOT marked read on the way — the organiser
                        // has not seen the request.
                        if (!notification.read && requestOpenMarksRead(type)) {
                          markNotificationAsReadAction(notification.logId);
                        }
                        onNavigateAction?.();
                        navigate(href);
                      }
                    : undefined
                }
                icon={isRequest ? requestIcon(type) : undefined}
                subjectName={isRequest ? notification.subjectName : undefined}
                companionCount={isRequest ? (notification.companionCount ?? 0) : undefined}
                key={notification.logId}
                dataLogId={notification.logId}
                disabledButtons={
                  !id || id === "000000000000000000000000" || chipBusy
                }
                read={notification.read}
                triggered={notification.createdAt}
                notificationChip={
                  ["EVENT_ROLE_UPDATED", "COMPANY_ROLE_CHANGED"].includes(
                    type
                  ) && `${notification.role}`
                }
                type={type}
                responsible={responsible}
                actorless={notification.actorless ?? TYPES_WITHOUT_ACTOR.has(type)}
                place={placeLabel}
                placeUnresolved={placeUnresolved}
                acceptText={
                  [
                    "EVENT_INVITATION",
                    "EVENT_TRANSFER_INVITATION",
                    "COMPANY_INVITATION",
                  ].includes(type) && t(`${type}-ACCEPT`)
                }
                rejectText={
                  [
                    "EVENT_INVITATION",
                    "EVENT_TRANSFER_INVITATION",
                    "COMPANY_INVITATION",
                  ].includes(type) && t(`${type}-REJECT`)
                }
                actionAccept={async () => {
                  await runChipAction(async () => {
                  const result = await actionAccept(type, id, notification);
                  if (result === "transferNeedsSubscription") {
                    setTransferUpgradeOpen(true);
                    return;
                  }
                  if (result === "companyChanged") {
                    window.location.href = "/";
                    return;
                  }
                  if (
                    result &&
                    typeof result === "object" &&
                    "kind" in result &&
                    (result.kind === "companyChangeFailed" ||
                      result.kind === "actionFailed")
                  ) {
                    // Previously a bare `return` (company switch) or nothing at
                    // all (event accept): the chip swallowed the failure with no
                    // toast, no state, nothing. The user clicked, the POST 400'd,
                    // and the UI did not move — indistinguishable from a dead
                    // button. Re-throwing inside the action only stops the
                    // notification from vanishing; the message lands in
                    // `notificationState.error`, which nothing in the repo
                    // renders. This is the half that reaches the user.
                    showToast(
                      describeFailure(
                        result,
                        result.kind === "companyChangeFailed"
                          ? t("joinCompanyFailed")
                          : tErrors("unknown"),
                      ),
                      "error",
                    );
                    return;
                  }
                  if (result && typeof result === "object" && "preflight" in result) {
                    setPreflightData(result);
                  }
                  });
                }}
                actionReject={async () => {
                  await runChipAction(async () => {
                    const result = await actionReject(type, id, notification);
                    // A failed decline keeps its notification (correct) — say so,
                    // otherwise the row just sits there and the click looks dead.
                    if (result && result.kind === "actionFailed") {
                      showToast(describeFailure(result, tErrors("unknown")), "error");
                    }
                  });
                }}
              />
            );
          })
        )}

        {/* Loading indicator */}
        {notificationState.loading && (
          <div className="py-4 text-center">
            <div
              className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"
              role="status"
            >
              <span className="sr-only">{t("loading")}</span>
            </div>
            <p className="body-3-regular mt-2 font-secondary">
              {t("furtherNotificationsLoading")}
            </p>
          </div>
        )}
          </>
        )}
      </div>
    </Card>,
    document.body,
  );

  return (
    <>
      {notificationPanel}
      {preflightData && (
        // Keyed by the notification: another invitation's dialog starts fresh
        // — its tick (PB-CDP-7), its error, its spinner (JoinCompanyDialog).
        <JoinCompanyDialog
          key={preflightData.notificationId}
          result={preflightData}
          onCloseAction={() => setPreflightData(null)}
          confirmCompanyJoin={confirmCompanyJoin}
          describeFailure={describeFailure}
        />
      )}
      {transferUpgradeOpen && (
        <PDeleteConfirmation
          title={t("transferUpgradeTitle")}
          message={t("transferUpgradeMessage")}
          onCloseAction={() => setTransferUpgradeOpen(false)}
          onConfirmAction={async () => {
            setTransferUpgradeOpen(false);
            navigate("/user/subscription/plan");
          }}
          isLoading={false}
          primaryButtonText={t("transferUpgradeCta")}
        />
      )}
    </>
  );
};
